// ─────────────────────────────────────────────────────────────────────────────
// Crew — onboarding: create a group OR join one by invite code (Agent B).
//
// GUARDRAIL: group formation is NEVER gated by monetisation. Creating/joining is
// always free. A display name is required (it's how teammates recognise you). On
// create we surface the invite code PROMINENTLY with a copy button so the group can
// actually grow — that growth is the whole point of the funnel.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, type FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import { Button } from '../components/Button';
import { Card, CardTitle } from '../components/Card';
import { TextField } from '../components/Field';
import { ErrorBanner } from '../components/Feedback';
import { errorMessage } from '../lib/format';

interface OnboardingProps {
  // Called once the user is in a group; the parent reloads session + routes to it.
  onJoined: () => void;
}

type Mode = 'create' | 'join';

export function Onboarding({ onJoined }: OnboardingProps) {
  const [mode, setMode] = useState<Mode>('create');

  // create
  const [groupName, setGroupName] = useState('');
  const [createName, setCreateName] = useState('');
  // join
  const [inviteCode, setInviteCode] = useState('');
  const [joinName, setJoinName] = useState('');

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // After a successful create we hold the invite code so the user can share it
  // before moving on.
  const [createdCode, setCreatedCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!groupName.trim() || !createName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.createGroup({ name: groupName.trim(), displayName: createName.trim() });
      setCreatedCode(res.inviteCode);
    } catch (err) {
      setError(errorMessage(err, 'Could not create the group. Please try again.'));
    } finally {
      setBusy(false);
    }
  }

  async function handleJoin(e: FormEvent) {
    e.preventDefault();
    if (!inviteCode.trim() || !joinName.trim()) return;
    setBusy(true);
    setError(null);
    try {
      await api.joinGroup({ inviteCode: inviteCode.trim().toUpperCase(), displayName: joinName.trim() });
      onJoined();
    } catch (err) {
      if (err instanceof ApiError && err.status === 402) {
        setError('This group is full on the free tier. Ask the owner to make room or start your own.');
      } else if (err instanceof ApiError && err.status === 404) {
        setError('That invite code didn’t match a group. Double-check it and try again.');
      } else {
        setError(errorMessage(err, 'Could not join the group. Please try again.'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function copyCode() {
    if (!createdCode) return;
    try {
      await navigator.clipboard.writeText(createdCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* clipboard blocked — the code is still visible to copy by hand */
    }
  }

  // ── Post-create: show the invite code prominently, then continue ────────────
  if (createdCode) {
    return (
      <div className="mx-auto flex max-w-md flex-col gap-6 py-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-ink-900">You’re all set</h1>
          <p className="mt-1 text-ink-700">
            Share this invite code so your crew can join. They’ll enter it on the join screen.
          </p>
        </div>
        <Card className="text-center">
          <p className="text-sm font-medium text-ink-700">Invite code</p>
          <p className="mt-2 select-all font-mono text-3xl font-bold tracking-[0.3em] text-coral-600">
            {createdCode}
          </p>
          <div className="mt-4 flex justify-center">
            <Button variant="secondary" onClick={copyCode}>
              {copied ? 'Copied!' : 'Copy code'}
            </Button>
          </div>
        </Card>
        <Button onClick={onJoined}>Go to your group</Button>
      </div>
    );
  }

  return (
    <div className="mx-auto flex max-w-md flex-col gap-6 py-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-ink-900">Start a crew</h1>
        <p className="mt-1 text-ink-700">
          Create a new group or join one with an invite code. It’s always free.
        </p>
      </div>

      <div
        role="tablist"
        aria-label="Create or join"
        className="grid grid-cols-2 gap-1 rounded-xl2 bg-sand-100 p-1"
      >
        {(['create', 'join'] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            onClick={() => {
              setMode(m);
              setError(null);
            }}
            className={
              'min-h-[44px] rounded-xl2 text-sm font-semibold transition focus-visible:outline-none ' +
              'focus-visible:ring-2 focus-visible:ring-coral-500 ' +
              (mode === m ? 'bg-white text-ink-900 shadow-sm' : 'text-ink-700/70 hover:text-ink-800')
            }
          >
            {m === 'create' ? 'Create group' : 'Join group'}
          </button>
        ))}
      </div>

      {error && <ErrorBanner message={error} />}

      {mode === 'create' ? (
        <Card>
          <CardTitle>Create a new group</CardTitle>
          <form onSubmit={handleCreate} className="mt-4 flex flex-col gap-4">
            <TextField
              label="Group name"
              required
              value={groupName}
              onChange={(e) => setGroupName(e.target.value)}
              placeholder="e.g. Weekend Wanderers"
            />
            <TextField
              label="Your display name"
              required
              value={createName}
              onChange={(e) => setCreateName(e.target.value)}
              placeholder="How your crew sees you"
              hint="Required — it’s how teammates recognise you."
            />
            <Button type="submit" busy={busy} disabled={!groupName.trim() || !createName.trim()}>
              Create group
            </Button>
          </form>
        </Card>
      ) : (
        <Card>
          <CardTitle>Join with an invite code</CardTitle>
          <form onSubmit={handleJoin} className="mt-4 flex flex-col gap-4">
            <TextField
              label="Invite code"
              required
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value)}
              placeholder="e.g. CREW42"
              autoCapitalize="characters"
              className="[&_input]:font-mono [&_input]:tracking-widest"
            />
            <TextField
              label="Your display name"
              required
              value={joinName}
              onChange={(e) => setJoinName(e.target.value)}
              placeholder="How your crew sees you"
              hint="Required — it’s how teammates recognise you."
            />
            <Button type="submit" busy={busy} disabled={!inviteCode.trim() || !joinName.trim()}>
              Join group
            </Button>
          </form>
        </Card>
      )}
    </div>
  );
}
